import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  BadgeDollarSign,
  Calendar,
  CreditCard,
  Info,
  RefreshCw,
} from "lucide-react";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import { ApplicationModalLoadingState } from "../ApplicationListLoadingState";
import {
  getCreditReportAddressRequest,
  regenerateCreditReport,
} from "../../services/applicationService";
import { getStates, type State } from "@/lib/api/statesService";
import { showError, showSuccess } from "@/lib/utils/notifications";
import type { CreditReportAddressModalProps, AddressRow } from "../../types/modals";
import modalStyles from "../../styles/modals.module.css";
import {
  addressKindLabel,
  addressRowHasDisplayContent,
  buildCreditReportAddressModalState,
  buildRegenerateCreditReportParams,
  isTruthyFlag,
  resolveAddressRowRadioValue,
} from "../../utils/creditReportAddressModalLogic";

const FIELD_INPUT_CLASS =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20";

function formatAddressLine(row: AddressRow): string {
  const parts = [row.house_no, row.street, row.city, row.state, row.zip_code]
    .map((part) => (part != null && String(part).trim() !== "" ? String(part).trim() : ""))
    .filter(Boolean);
  return parts.join(", ");
}

function AddressOption({
  id,
  name,
  value,
  checked,
  onChange,
  title,
  subtitle,
  warning,
  testId,
}: {
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  title: string;
  subtitle: string;
  warning?: boolean;
  testId: string;
}) {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 ${
        warning ? "bg-amber-50/60" : ""
      }`}
      data-testid={`${testId}-label`}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="mt-1 h-4 w-4 shrink-0 accent-blue-600"
        value={value}
        checked={checked}
        onChange={onChange}
        data-testid={`${testId}-radio`}
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block text-sm text-slate-500">{subtitle}</span>
        {warning ? (
          <span className="mt-1 block text-xs font-medium text-amber-800">
            Address Discrepancy
          </span>
        ) : null}
      </span>
    </label>
  );
}

function SkipOption({
  checked,
  onChange,
  icon,
  label,
  testId,
}: {
  checked: boolean;
  onChange: () => void;
  icon: ReactNode;
  label: string;
  testId: string;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
      data-testid={`${testId}-label`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onChange}
        data-testid={`${testId}-toggle`}
      />
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
          checked ? "border-blue-600" : "border-slate-300 bg-white"
        }`}
        aria-hidden="true"
      >
        {checked ? <span className="h-2 w-2 rounded-full bg-blue-600" /> : null}
      </span>
      <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-slate-700">
        <span className="text-blue-600">{icon}</span>
        {label}
      </span>
    </label>
  );
}

export function CreditReportAddressModal({
  isOpen,
  onClose,
  applicantId,
  creditReportFiles,
  savedAlternateAddresses,
  creditReportHistory,
  onDone,
}: CreditReportAddressModalProps) {
  const tid = "application-modal-credit-report-address";
  const [addresses, setAddresses] = useState<AddressRow[]>([]);
  const [statesList, setStatesList] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addressId, setAddressId] = useState("");
  const [skipOption, setSkipOption] = useState<"" | "skip_ssn" | "skip_dob">("");
  const [reportType, setReportType] = useState(creditReportFiles || "json");
  const [altStreet, setAltStreet] = useState("");
  const [altCity, setAltCity] = useState("");
  const [altState, setAltState] = useState("");
  const [altZip, setAltZip] = useState("");

  const allAddressUse = useMemo(() => {
    if (!addresses.length) return false;
    return addresses.every((a) => isTruthyFlag(a.credit_report));
  }, [addresses]);

  const alternateInvalid =
    addressId === "OTHER" &&
    (!altStreet.trim() ||
      !altCity.trim() ||
      !altState.trim() ||
      !String(altZip).trim());

  const applyModalState = useCallback(
    (apiResponse: unknown) => {
      const initial = buildCreditReportAddressModalState({
        apiResponse,
        profileAlternates: savedAlternateAddresses,
        creditReportHistory,
      });
      setAddresses(initial.addresses);
      setAddressId(initial.addressId);
      setAltStreet(initial.altStreet);
      setAltCity(initial.altCity);
      setAltState(initial.altState);
      setAltZip(initial.altZip);
    },
    [creditReportHistory, savedAlternateAddresses],
  );

  const loadAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const apiResponse = await getCreditReportAddressRequest(applicantId);
      applyModalState(apiResponse);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : "Failed to load addresses.");
      applyModalState(null);
    } finally {
      setLoading(false);
    }
  }, [applicantId, applyModalState]);

  useEffect(() => {
    if (!isOpen) return;
    setReportType(creditReportFiles || "json");
    setSkipOption("");
    void loadAddresses();
    void getStates({ zip_flag: "1" })
      .then(setStatesList)
      .catch(() => setStatesList([]));
  }, [isOpen, creditReportFiles, loadAddresses]);

  const submit = async () => {
    if (addressId === "OTHER" && alternateInvalid) {
      showError("Please complete street, city, state, and zip for the alternate address.");
      return;
    }

    const params = buildRegenerateCreditReportParams({
      addressId,
      addresses,
      otherFields: {
        street: altStreet,
        city: altCity,
        state: altState,
        zip: altZip,
      },
      skipOption,
      reportType,
    });

    setSubmitting(true);
    try {
      await regenerateCreditReport(applicantId, params);
      showSuccess(
        "Credit report has been fetched. Please check Notes if the credit report fails.",
      );
      await onDone();
      onClose();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : "Failed to regenerate credit report.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled =
    submitting || loading || (addressId === "OTHER" && alternateInvalid);

  const toggleSkipOption = (option: "skip_ssn" | "skip_dob") => {
    setSkipOption((current) => (current === option ? "" : option));
  };

  return (
    <OnScreenModal
      isOpen={isOpen}
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      title="Resubmit Credit Report"
      onClose={onClose}
      maxWidth="max-w-3xl"
      headerVariant="iconTitle"
      titleIcon={<BadgeDollarSign className="h-[18px] w-[18px]" />}
      footerAlign="end"
      bodyClassName="!max-h-none overflow-visible pb-1"
      bodyScrollOnly
      footer={
        <div
          className={`${modalStyles.modalFooterActionGroup} ${modalStyles.modalFooterActionGroupEnd}`}
        >
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            disabled={submitting}
            onClick={onClose}
            data-testid="application-modal-credit-report-address-cancel-button"
          >
            Cancel
          </button>
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            disabled={submitDisabled}
            onClick={() => void submit()}
            data-testid="application-modal-credit-report-address-resubmit-button"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {submitting ? "Submitting…" : "Resubmit Credit Report"}
          </button>
        </div>
      }
    >
      <div className="px-1 py-1 text-sm text-slate-800" data-testid={`${tid}-body`}>
        {loading ? (
          <ApplicationModalLoadingState
            testId={`${tid}-loading`}
            className="min-h-32"
          />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <section>
              <div className="mb-3 flex items-center gap-2">
                <h3 className="text-sm font-semibold text-slate-900">Select Address</h3>
                <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-600">
                  Optional
                </span>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                {addresses.map((row, idx) => {
                  if (!addressRowHasDisplayContent(row)) return null;
                  const value = resolveAddressRowRadioValue(row, idx);
                  const id = `resubmit-credit-addr-${value.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
                  return (
                    <AddressOption
                      key={`${value}-${idx}`}
                      id={id}
                      name="resubmit_credit_address"
                      value={value}
                      checked={addressId === value}
                      onChange={() => setAddressId(value)}
                      title={addressKindLabel(row.current_address)}
                      subtitle={formatAddressLine(row)}
                      warning={isTruthyFlag(row.credit_report)}
                      testId={`application-modal-credit-report-address-row-${value}`}
                    />
                  );
                })}
                <AddressOption
                  id="resubmit-credit-addr-other"
                  name="resubmit_credit_address"
                  value="OTHER"
                  checked={addressId === "OTHER"}
                  onChange={() => setAddressId("OTHER")}
                  title="Alternate Address"
                  subtitle="Enter alternate address manually"
                  testId="application-modal-credit-report-address-row-other"
                />
              </div>

              {addressId === "OTHER" && (
                <div className="mt-4 grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="resubmit-alt-street"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500"
                      data-testid="application-modal-credit-report-address-alt-street-label"
                    >
                      Street <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="resubmit-alt-street"
                      className={FIELD_INPUT_CLASS}
                      value={altStreet}
                      onChange={(e) => setAltStreet(e.target.value)}
                      placeholder="Street"
                      autoComplete="off"
                      data-testid="application-modal-credit-report-address-alt-street-input"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="resubmit-alt-city"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500"
                      data-testid="application-modal-credit-report-address-alt-city-label"
                    >
                      City <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="resubmit-alt-city"
                      className={FIELD_INPUT_CLASS}
                      value={altCity}
                      onChange={(e) => setAltCity(e.target.value)}
                      placeholder="City"
                      autoComplete="off"
                      data-testid="application-modal-credit-report-address-alt-city-input"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="resubmit-alt-state"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500"
                      data-testid="application-modal-credit-report-address-alt-state-label"
                    >
                      State <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="resubmit-alt-state"
                      className={FIELD_INPUT_CLASS}
                      value={altState}
                      onChange={(e) => setAltState(e.target.value)}
                      data-testid="application-modal-credit-report-address-alt-state-select"
                    >
                      <option value="">Select State</option>
                      {statesList.map((s) => (
                        <option key={s.id} value={s.state_code}>
                          {s.state_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="resubmit-alt-zip"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500"
                      data-testid="application-modal-credit-report-address-alt-zip-label"
                    >
                      Zipcode <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="resubmit-alt-zip"
                      className={FIELD_INPUT_CLASS}
                      value={altZip}
                      onChange={(e) => setAltZip(e.target.value)}
                      placeholder="Zipcode"
                      inputMode="numeric"
                      maxLength={10}
                      autoComplete="off"
                      data-testid="application-modal-credit-report-address-alt-zip-input"
                    />
                  </div>
                </div>
              )}

              {addresses.length > 0 && allAddressUse && (
                <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Equifax reported address discrepancy for all the available address for this
                  applicant.
                </p>
              )}
            </section>

            <div className="space-y-6">
              <section>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Additional Options</h3>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <SkipOption
                    checked={skipOption === "skip_ssn"}
                    onChange={() => toggleSkipOption("skip_ssn")}
                    icon={<CreditCard className="h-4 w-4" aria-hidden="true" />}
                    label="Remove SSN for resubmit"
                    testId="application-modal-credit-report-address-skip-ssn"
                  />
                  <SkipOption
                    checked={skipOption === "skip_dob"}
                    onChange={() => toggleSkipOption("skip_dob")}
                    icon={<Calendar className="h-4 w-4" aria-hidden="true" />}
                    label="Remove DOB for resubmit"
                    testId="application-modal-credit-report-address-skip-dob"
                  />
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Report Type</h3>
                <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-4">
                  <div className="flex items-start gap-3">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
                    <div>
                      <div
                        className="text-sm font-semibold text-slate-900"
                        data-testid="application-modal-credit-report-address-report-type-json-label"
                      >
                        JSON
                      </div>
                      <p className="mt-1 text-sm text-slate-600">
                        Credit report will be generated in JSON format.
                      </p>
                      <input
                        type="radio"
                        name="resubmit_credit_report_type"
                        className="sr-only"
                        checked={reportType === "json"}
                        onChange={() => setReportType("json")}
                        data-testid="application-modal-credit-report-address-report-type-json-radio"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </OnScreenModal>
  );
}
